/* The SHOW control (Ale rulings 2026-09-03 C8 + 2026-09-04 R3) — one
 * control in the fund header, right of the tabs:
 *
 *     SHOW · today · evidenced only · ‹ [as of 31 May 2026 ▾] ›
 *
 * Three ways to read the record, one concept — the chain under a
 * condition: TODAY (the record as it stands), EVIDENCED ONLY (the objects
 * whose demands are met), AS OF A DATE (the record as it was known on a
 * day — the engine's one rule, R3, served by /as-of). The date segment is
 * a house-style date button opening a small calendar (AsOfPicker); the ‹ ›
 * steps walk the dates the record holds — a stated balance or a movement
 * — and the last step forward is today. The as-of date lives in the URL
 * (`?asof=`), so the officer can return to it; the other two are a lens
 * the caller resets on every fund open.
 *
 * REACH (Ale 14b): the control governs the three tab contents. The
 * counts strip, the workbench queue and Export stay on the full record.
 * Every date the officer sees is the canon "31 May 2026" — never dd/mm. */

import { useEffect, useRef, useState } from 'react'
import { stepDates, type HistoryDate, type RebuildMode } from './rebuildRecord'
import { formatOfficerDate } from '../../lib/officerFormat'
import { HelpDot } from '../guidance/Guidance'
import AsOfPicker from './AsOfPicker'

export interface RebuildState {
  mode: RebuildMode
  asOf: string | null
}

interface Props {
  value: RebuildState
  onChange: (next: RebuildState) => void
  /** The dates the record holds (historyDates) — the steps and the
   *  picker's dots and chips. */
  dates: HistoryDate[]
  today: string
}

export default function RebuildToggle({ value, onChange, dates, today }: Props) {
  const [open, setOpen] = useState(false)
  // The date the as-of segment keeps showing while another segment is
  // active, so the officer's last choice is one click away.
  const [draft, setDraft] = useState<string | null>(value.asOf)
  useEffect(() => {
    if (value.asOf) setDraft(value.asOf)
  }, [value.asOf])
  const rootRef = useRef<HTMLDivElement>(null)
  const dateBtnRef = useRef<HTMLButtonElement>(null)

  /* A click outside closes the popover; Esc inside returns focus to the
   * date button (the popover handles its own Esc). */
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const close = () => {
    setOpen(false)
    dateBtnRef.current?.focus()
  }
  const goToday = () => {
    onChange({ mode: 'full', asOf: null })
    close()
  }
  const pick = (iso: string) => {
    if (iso === today) {
      goToday()
      return
    }
    setDraft(iso)
    onChange({ mode: 'asof', asOf: iso })
    close()
  }

  const current = value.mode === 'asof' ? value.asOf : null
  const { prev, next } = stepDates(
    dates.map((d) => d.date),
    current,
    today
  )
  const step = (iso: string | null) => {
    if (!iso) return
    if (iso === today) onChange({ mode: 'full', asOf: null })
    else pick(iso)
  }
  const shown = current ?? draft
  const label = shown ? `as of ${formatOfficerDate(shown)}` : 'as of a date'

  return (
    <div className="fa-rebuild" data-testid="rebuild-toggle" ref={rootRef}>
      <span className="fa-lbl">show</span>
      <div className="fa-rbg" role="group" aria-label="show the record">
        <button
          type="button"
          className={`fa-rbseg${value.mode === 'full' ? ' on' : ''}`}
          onClick={() => onChange({ mode: 'full', asOf: null })}
          aria-pressed={value.mode === 'full'}
          data-testid="rebuild-full"
        >
          today
        </button>
        <button
          type="button"
          className={`fa-rbseg${value.mode === 'evidenced' ? ' on' : ''}`}
          onClick={() => onChange({ mode: 'evidenced', asOf: null })}
          aria-pressed={value.mode === 'evidenced'}
          data-testid="rebuild-evidenced"
        >
          evidenced only
        </button>
        <button
          type="button"
          className="fa-rbseg fa-rbstep"
          onClick={() => step(prev)}
          disabled={!prev}
          aria-label={prev ? `as of ${formatOfficerDate(prev)}` : 'no earlier date on record'}
          title={prev ? `as of ${formatOfficerDate(prev)}` : undefined}
          data-testid="rebuild-asof-prev"
        >
          ‹
        </button>
        <button
          type="button"
          ref={dateBtnRef}
          className={`fa-rbseg fa-rbdate${value.mode === 'asof' ? ' on' : ''}`}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') step(prev)
            else if (e.key === 'ArrowRight') step(next)
          }}
          aria-pressed={value.mode === 'asof'}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid="rebuild-asof"
        >
          <i className="fa-cal-glyph" aria-hidden="true" />
          {label}
          <i className="fa-caret" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="fa-rbseg fa-rbstep"
          onClick={() => step(next)}
          disabled={!next}
          aria-label={
            next ? (next === today ? 'back to today' : `as of ${formatOfficerDate(next)}`) : 'today'
          }
          title={next && next !== today ? `as of ${formatOfficerDate(next)}` : undefined}
          data-testid="rebuild-asof-next"
        >
          ›
        </button>
      </div>
      {value.mode === 'asof' ? (
        <div className="fa-rbsteps" data-testid="rebuild-asof-steps">
          {prev ? (
            <button type="button" className="fa-rbstepcap" onClick={() => step(prev)}>
              ‹ {formatOfficerDate(prev)}
            </button>
          ) : null}
          {prev && next ? <span className="sep">·</span> : null}
          {next ? (
            <button type="button" className="fa-rbstepcap" onClick={() => step(next)}>
              {next === today ? 'today' : formatOfficerDate(next)} ›
            </button>
          ) : null}
        </div>
      ) : null}
      {open ? (
        <AsOfPicker
          value={current}
          today={today}
          dates={dates}
          onPick={pick}
          onToday={goToday}
          onClose={close}
        />
      ) : null}
      <HelpDot k="fund.rebuild" />
    </div>
  )
}
